import * as dates from '@simplybenefits/sb-dates';
import { localizationMaps } from '@simplybenefits/sb-ui-library';

/**
 * Parses and formats a complete user data object to be used in a user settings update
 *
 * @param {Object} data - The user data to be parsed and formatted.
 *
 * @returns {Object} The formatted user data.
 */
const parseData = (data) => ({
    firstname: data.firstname.trim(),
    lastname: data.lastname.trim(),
    email: data.email.trim(),
    addressLineOne: data.addressLineOne.trim(),
    addressLineTwo: data.addressLineTwo ? data.addressLineTwo.trim() : null,
    city: data.city.trim(),
    postalCode: data.postalCode.trim(),
    province: localizationMaps.toEnglish("provinces", data.province).trim(), 
    birthdate: data.birthdate,
    phone: data.phone.trim(),
    maritlStat: localizationMaps.toEnglish('maritalStatuses', data.maritlStat.trim()),
    gender: localizationMaps.toEnglish("genders", data.gender).trim(),
    coverageType: localizationMaps.toEnglish("coverageTypes", data.coverageType.trim()),
    language: data.language,
    classID: data.classID,
    useMFA: Boolean(data.useMFA)
});

/**
 * Merges old and new user data, giving precedence to new data details.
 *
 * @param {Object} oldData - The old user data.
 * @param {Object} newData - The new user data details.
 *
 * @returns {Object} The merged user data.
 */
const mergeAndParseUserData = (oldData, newData) => parseData({ ...oldData, ...newData });

/**
 * Checks if there are any changes in the user's personal information compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: firstname, lastname, email, birthdate, phone, and marital status.
 * It returns an object containing the fields that have changed along with their new values. If no changes
 * are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnChangeRequestChanges = (userData, initialData) => {
    const changes = {};

    if (userData.firstname !== initialData.firstname) {
        changes.firstname = userData.firstname;
    }

    if (userData.lastname !== initialData.lastname) {
        changes.lastname = userData.lastname;
    }

    if (userData.email !== initialData.email) {
        changes.email = userData.email;
    }

    if (dates.getUTC(userData.birthdate) !== initialData.birthdate) {
        changes.birthdate = dates.getUTC(userData.birthdate);
    }

    if (localizationMaps.toEnglish('maritalStatuses', userData.maritlStat) !== initialData.maritlStat) {
        changes.maritlStat = localizationMaps.toEnglish('maritalStatuses', userData.maritlStat);
    }

    return Object.keys(changes).length ? changes : false;
};

/**
 * Checks if there are any changes in the user's additional details compared to the initial data.
 * 
 * This function compares the current user data with the initial data to determine if there are any
 * differences in the following fields: divisionName, title, province, earningsType, salary, hourlyWage,
 * hoursPerWeek, language, payrollID, and waiveEligibilityPeriod. It returns an object containing the fields
 * that have changed along with their new values. If no changes are found, it returns an empty object.
 * 
 * @param {Object} userData - The current user data.
 * @param {Object} initialData - The initial user data.
 * @returns {Object} - An object containing the changed fields and their new values, or an empty object if no changes are found.
 */
const returnRegularInfoChanges = (userData, initialData) => {
    const changes = {};

    if (localizationMaps.toEnglish('provinces', userData.province) !== initialData.province) {
        changes.province = localizationMaps.toEnglish('provinces', userData.province);
    }

    if (localizationMaps.toEnglish('genders', userData.gender) !== initialData.gender) {
        changes.gender = localizationMaps.toEnglish('genders', userData.gender);
    }

    if (userData.addressLineOne !== initialData.addressLineOne) {
        changes.addressLineOne = userData.addressLineOne;
    }

    if (userData.addressLineTwo && userData.addressLineTwo !== initialData.addressLineTwo) {
        changes.addressLineTwo = userData.addressLineTwo;
    }

    if (!userData.addressLineTwo && initialData.addressLineTwo) {
        changes.addressLineTwo = null;
    }

    if (userData.postalCode !== initialData.postalCode) {
        changes.postalCode = userData.postalCode;
    }

    if (userData.city !== initialData.city) {
        changes.city = userData.city;
    }

    if (userData.language === 'English' && initialData.language === 'fr') {
        changes.language = 'en';
    }

    if (userData.language !== 'English' && initialData.language === 'en') {
        changes.language = 'fr';
    }

    if (userData.phone !== initialData.phone) {
        changes.phone = userData.phone;
    }

    if (userData.useMFA !== initialData.useMFA) {
        changes.useMFA = userData.useMFA;
    }

    return Object.keys(changes).length ? changes : false;
};

export { mergeAndParseUserData, returnChangeRequestChanges, returnRegularInfoChanges };